import { z } from "zod";
import { bookendFonts } from "./font.js";
import { colorSchema } from "./primitives.js";

/**
 * How one line on a card is set.
 *
 * Every field is optional and every fallback lives in `bookendTextDefaults`,
 * because a card written before this existed carries none of them and has to
 * keep rendering exactly as it did. Setting one writes a concrete value and
 * only that value changes.
 *
 * `bold` and `italic` rather than a numeric weight and a font-style string:
 * these are the three buttons anyone expects on a title, and a card is not the
 * place to explain the difference between 500 and 600.
 */
export const textStyleSchema = z.object({
  /** A CSS font stack, normally the `stack` of one of `bookendFonts`. */
  fontFamily: z.string().max(160).optional(),
  /** Composition pixels, in the fixed 1920-wide space, like `frameSchema.radius`. */
  fontSize: z.number().min(8).max(300).optional(),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  strikethrough: z.boolean().optional(),
  color: colorSchema.optional(),
});

/**
 * What a card looks like when nothing has been set on it.
 *
 * These are the values the renderer hard-coded before any of this was
 * editable, kept here so the panel can show what is actually on screen instead
 * of a second opinion about it. The two weights per line are what `bold` picks
 * between; the defaults reproduce the old fixed 800 and 500.
 */
export const bookendTextDefaults = {
  title: {
    fontFamily: bookendFonts[0].stack,
    fontSize: 88,
    bold: true,
    italic: false,
    strikethrough: false,
    color: "#f8fafc",
    weights: { bold: 800, normal: 500 },
  },
  subtitle: {
    fontFamily: bookendFonts[0].stack,
    fontSize: 38,
    bold: false,
    italic: false,
    strikethrough: false,
    /** Solid here; the renderer softens an *unset* subtitle the way it always did. */
    color: "#e2e8f0",
    weights: { bold: 700, normal: 500 },
  },
} as const;

/** What the renderer draws a subtitle in when nothing has been chosen. */
export const legacySubtitleColor = "rgba(226,232,240,0.78)";

export type TextStyle = z.infer<typeof textStyleSchema>;

/**
 * A project's name, as a filename somebody would not be embarrassed to send.
 *
 * Exports were named `render-<uuid>.mp4`, which is the storage key wearing a
 * file extension — fine for a bucket, useless in a downloads folder full of
 * them. The video is the thing the project was for, so it takes the project's
 * name.
 *
 * Conservative about what survives, because this string ends up in a
 * `Content-Disposition` header and in a filesystem: letters, digits, spaces and
 * hyphens, collapsed. Quotes, semicolons and newlines would all end a header
 * value early, which is a header-injection hole as much as it is an ugly name.
 * Non-Latin scripts are dropped rather than transliterated — a name that
 * reduces to nothing falls back rather than producing a file called `-.mp4`.
 */
export function downloadFilename(projectName: string, extension: string): string {
  const stem = projectName
    .normalize("NFKD")
    // Strip the accents NFKD just separated, so "Café" becomes "Cafe" rather
    // than losing the letter entirely below.
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9 _-]+/g, " ")
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    // Long enough for a real title, short of the 255 bytes a filesystem stops
    // at once the extension and a browser's " (1)" are added.
    .slice(0, 120)
    .replace(/-+$/, "");

  const safeExtension = extension.replace(/[^a-zA-Z0-9]/g, "").toLowerCase() || "mp4";
  return `${stem || "veyso-video"}.${safeExtension}`;
}
